/* eslint-disable react/jsx-sort-props */
// @flow

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { log } from '../utils/jsUtils';
import { selectSnackMessage } from '../reduxStore/modules/common/selectors';
import { setSnackMessage } from '../reduxStore/modules/common/actions';

import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ShowSnackMsg = () => {
  const theme = useTheme();
  const [isSnackOpen, setIsSnackOpen] = useState(false);
  const [snackData, setSnackData] = useState({
    message: '',
    level: 'success',
    duration: 3000,
    action: null // { label: string, onClick: Function }
  });
  const { message, level, duration, action } = snackData;

  const snackMessage = useSelector(selectSnackMessage);
  const dispatch = useDispatch();

  const closeSnack = () => {
    setIsSnackOpen(false);
    // We've already cleared it from Redux in useEffect,
    // so no need to do it a second time—optional if you want:
    // dispatch(setSnackMessage(null));
  };

  const styles = useMemo(() => ({
    success: { backgroundColor: theme.palette.status.finished.main },
    error: { backgroundColor: theme.palette.error.main },
    info: { backgroundColor: theme.palette.status.info.main },
    warning: { backgroundColor: theme.palette.status.current.main },
    icon: { fontSize: 20 },
    iconVariant: { opacity: 0.9, marginRight: 16 },
    message: { display: 'flex', alignItems: 'center' }
  }), [theme]);

  const variantIcon = useMemo(
    () => ({
      success: CheckCircleIcon,
      warning: WarningIcon,
      error: ErrorIcon,
      info: InfoIcon
    }),
    []
  );
  const Icon = variantIcon[level] || InfoIcon;

  // Clear Redux the moment we pick up a new snackMessage
  useEffect(() => {
    if (snackMessage?.message) {
      log('[ShowSnackMsg] snackMessage:', snackMessage);

      // Support both 'level' and 'variant' for backwards compatibility
      const messageLevel = snackMessage.level || snackMessage.variant || 'info';

      setSnackData({
        message: snackMessage.message,
        level: messageLevel,
        duration: snackMessage.duration || 3000,
        action: snackMessage.action || null
      });
      setIsSnackOpen(true);

      // Immediately remove from Redux so it won't persist on page reload
      dispatch(setSnackMessage(null));
    }
  }, [snackMessage, dispatch]);

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      autoHideDuration={duration}
      onClose={closeSnack}
      open={isSnackOpen}
      ContentProps={{
        'aria-describedby': 'message-id'
      }}
    >
      <SnackbarContent
        style={styles[level] || styles.info}
        aria-describedby="client-snackbar"
        message={
          <span id="client-snackbar" style={styles.message}>
            <Icon style={styles.iconVariant} />
            {message}
          </span>
        }
        action={
          <>
            {action && (
              <Button
                color="inherit"
                size="small"
                onClick={action.onClick}
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                {action.label}
              </Button>
            )}
            <IconButton
              key="close"
              aria-label="Close"
              color="inherit"
              onClick={closeSnack}
            >
              <CloseIcon style={styles.icon} />
            </IconButton>
          </>
        }
      />
    </Snackbar>
  );
};

export default ShowSnackMsg;